import { FilterGroup } from '../components/FilterGroup';

/**
 * Operators that do NOT require a value input (presence checks + relative dates).
 */
export const VALUELESS_OPERATORS = new Set<string>([
    'null', 'not_null', 'empty', 'not_empty',
    'is_true', 'is_false',
    'today', 'yesterday', 'tomorrow',
    'this_week', 'last_week', 'next_week',
    'this_month', 'last_month', 'next_month',
    'this_year', 'last_year', 'next_year',
]);

export const operatorRequiresValue = (operator: string): boolean => !VALUELESS_OPERATORS.has(operator);

const LIST_OPERATORS = new Set<string>(['in', 'not_in']);

/**
 * A list operator wants an array. The picker already hands one over for
 * relations; a typed value is one string with commas, which the API would
 * silently drop rather than split.
 */
export const toApiValue = (operator: string, value: any): any => {
    if (!LIST_OPERATORS.has(operator) || Array.isArray(value)) return value;
    if (value === null || value === undefined) return value;
    return String(value).split(',').map(s => s.trim()).filter(s => s !== '');
};

/**
 * Normalizes filter conditions from API format to FilterGroup format
 */
export const normalizeConditions = (data: any): FilterGroup => {
    if (!data) {
        return {
            type: 'group',
            operator: 'AND',
            conditions: []
        };
    }

    const conditions = (data.conditions || []).map((c: any) => {
        if (c.columnId) {
            return {
                type: 'condition' as const,
                columnId: c.columnId,
                operator: c.operator,
                value: c.value
            };
        } else {
            return normalizeConditions(c);
        }
    });

    return {
        type: 'group' as const,
        operator: data.operator || 'AND',
        conditions
    };
};

/**
 * Builds filter groups from FilterGroup structure for API calls
 */
export const buildFiltersFromGroup = (group: FilterGroup | null): any => {
    if (!group || !group.conditions || group.conditions.length === 0) {
        return null;
    }

    const result: any = {
        operator: group.operator,
        conditions: []
    };

    group.conditions.forEach(condition => {
        if (condition.type === 'condition') {
            // Include if value is non-empty OR operator doesn't need a value (is_null, today, etc.)
            const v = condition.value;
            const hasValue = Array.isArray(v) ? v.length > 0 : (v !== '' && v !== null && v !== undefined);
            if (hasValue || !operatorRequiresValue(condition.operator)) {
                result.conditions.push({
                    columnId: condition.columnId,
                    operator: condition.operator,
                    value: toApiValue(condition.operator, v)
                });
            }
        } else {
            const subGroup = buildFiltersFromGroup(condition);
            if (subGroup) {
                result.conditions.push(subGroup);
            }
        }
    });

    return result.conditions.length > 0 ? result : null;
};
